import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

const apiUrl = import.meta.env.VITE_APP_API

function OrderManagement() {
    const navigate = useNavigate()
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [showOrderModal, setShowOrderModal] = useState(false)

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(10)

    // Filter state
    const [statusFilter, setStatusFilter] = useState('')
    const [searchTerm, setSearchTerm] = useState('')

    const access_token = JSON.parse(localStorage.getItem("faceAuthTokenData"))
        ?.token.access

    // API base URL
    const API_BASE_URL = `${apiUrl}/api/bookstore`

    // Fetch orders on component mount
    useEffect(() => {
        fetchOrders(1)
    }, [statusFilter, searchTerm])

    // Fetch orders with pagination and filters
    const fetchOrders = async (page = 1) => {
        try {
            setLoading(true)
            let url = `${API_BASE_URL}/orders/?page=${page}&page_size=${pageSize}`

            if (statusFilter) {
                url += `&status=${statusFilter}`
            }
            if (searchTerm) {
                url += `&search=${searchTerm}`
            }

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })

            if (response.data.results) {
                setOrders(response.data.results)
                setTotalPages(Math.ceil(response.data.count / pageSize))
                setTotalCount(response.data.count)
                setCurrentPage(page)
            } else {
                setOrders(response.data)
                setTotalPages(1)
                setTotalCount(response.data.length)
                setCurrentPage(1)
            }
        } catch (error) {
            console.error('Error fetching orders:', error)
            toast.error('Failed to fetch orders')
        } finally {
            setLoading(false)
        }
    }

    // Update order status
    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            await axios.patch(`${API_BASE_URL}/orders/${orderId}/`, {
                status: newStatus
            }, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })
            toast.success('Order status updated successfully!')
            fetchOrders(currentPage) // Refresh orders list
        } catch (error) {
            console.error('Error updating order status:', error)
            toast.error('Failed to update order status')
        }
    }

    // Download invoice
    const downloadInvoice = async (orderNumber) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/orders/${orderNumber}/invoice/`, {
                responseType: 'blob', // Important for file download
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            });
            
            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice_${orderNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Invoice downloaded successfully!');
        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast.error('Failed to download invoice');
        }
    };

    // Handle order details view
    const viewOrderDetails = (order) => {
        setSelectedOrder(order)
        setShowOrderModal(true)
    }

    // Handle page change
    const handlePageChange = (page) => {
        fetchOrders(page)
    }

    // Generate pagination buttons
    const renderPagination = () => {
        const pages = []
        const maxVisiblePages = 5
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1)
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 mx-1 rounded-md ${i === currentPage
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                >
                    {i}
                </button>
            )
        }

        return pages
    }

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800'
            case 'confirmed':
                return 'bg-blue-100 text-blue-800'
            case 'shipped':
                return 'bg-purple-100 text-purple-800'
            case 'delivered':
                return 'bg-green-100 text-green-800'
            case 'cancelled':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    // Get payment status badge color
    const getPaymentStatusBadgeColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'bg-green-100 text-green-800'
            case 'processing':
                return 'bg-blue-100 text-blue-800'
            case 'pending':
                return 'bg-yellow-100 text-yellow-800'
            case 'failed':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mx-auto">
                <h1 className="text-3xl font-bold text-gray-800 mb-8">Book Store Management</h1>

                {/* Navigation Buttons */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => navigate('/admin/add-book')}
                            className="bg-blue-500 text-white px-6 py-3 rounded-md font-medium shadow-md transition-all duration-200 hover:bg-blue-600 hover:shadow-lg"
                        >
                            📚 Book Management
                        </button>
                        <button
                            className="bg-green-500 text-white px-6 py-3 rounded-md font-medium shadow-md transition-all duration-200 hover:bg-green-600 hover:shadow-lg"
                        >
                            📦 Order Management
                        </button>
                    </div>
                </div>

                {/* Orders Management Section */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-700">Orders Management</h2>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600">
                                Total: {totalCount} orders
                            </span>
                            <button
                                onClick={() => fetchOrders(currentPage)}
                                disabled={loading}
                                className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-md transition-colors"
                            >
                                {loading ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search by Order Number
                            </label>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter order number..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filter by Status
                            </label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="PENDING">Pending</option>
                                <option value="CONFIRMED">Confirmed</option>
                                <option value="SHIPPED">Shipped</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => {
                                    setSearchTerm('')
                                    setStatusFilter('')
                                }}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <p className="mt-2 text-gray-500">Loading orders...</p>
                        </div>
                    ) : orders.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Order Number
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Customer
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Total Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Payment Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Payment Method
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Payment Reference
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {orders.map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        #{order.order_number}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {order.delivery_name || 'N/A'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {order.delivery_phone || 'N/A'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatDate(order.created_at)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    ৳{parseFloat(order.total || 0).toFixed(2)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
                                                        {order.status || 'Unknown'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadgeColor(order.payment_status)}`}>
                                                        {order.payment_status || 'Pending'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {order.payment_method || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {order.payment_reference ? (
                                                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                                            {order.payment_reference}
                                                        </span>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => viewOrderDetails(order)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            View
                                                        </button>
                                                        <button
                                                            onClick={() => downloadInvoice(order.order_number)}
                                                            className="text-green-600 hover:text-green-900"
                                                            title="Download Invoice"
                                                        >
                                                            📄
                                                        </button>
                                                        <select
                                                            value={order.status}
                                                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                                            className="text-xs px-2 py-1 border border-gray-300 rounded"
                                                        >
                                                            <option value="PENDING">Pending</option>
                                                            <option value="CONFIRMED">Confirmed</option>
                                                            <option value="SHIPPED">Shipped</option>
                                                            <option value="DELIVERED">Delivered</option>
                                                            <option value="CANCELLED">Cancelled</option>
                                                        </select>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-6 flex items-center justify-between">
                                    <div className="text-sm text-gray-700">
                                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>

                                        {renderPagination()}

                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-gray-500 mb-4">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                            <p className="text-gray-500">No orders match your current filters.</p>
                        </div>
                    )}
                </div>

                {/* Order Details Modal */}
                {showOrderModal && selectedOrder && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-semibold">Order Details - #{selectedOrder.order_number}</h3>
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={() => downloadInvoice(selectedOrder.order_number)}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Download Invoice</span>
                                    </button>
                                    <button
                                        onClick={() => setShowOrderModal(false)}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Order Information */}
                                <div>
                                    <h4 className="font-semibold mb-3">Order Information</h4>
                                    <div className="space-y-2 text-sm">
                                        <p><strong>Order Number:</strong> #{selectedOrder.order_number}</p>
                                        <p><strong>Date:</strong> {formatDate(selectedOrder.created_at)}</p>
                                        <p><strong>Status:</strong>
                                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(selectedOrder.status)}`}>
                                                {selectedOrder.status}
                                            </span>
                                        </p>
                                        <p><strong>Payment Status:</strong>
                                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadgeColor(selectedOrder.payment_status)}`}>
                                                {selectedOrder.payment_status || 'Pending'}
                                            </span>
                                        </p>
                                        <p><strong>Payment Method:</strong> {selectedOrder.payment_method || 'N/A'}</p>
                                        <p><strong>Delivery Method:</strong> {selectedOrder.delivery_method?.replace('_', ' ') || 'N/A'}</p>
                                        <p><strong>Subtotal:</strong> ৳{parseFloat(selectedOrder.subtotal || 0).toFixed(2)}</p>
                                        <p><strong>Shipping Charge:</strong> ৳{parseFloat(selectedOrder.shipping_charge || 0).toFixed(2)}</p>
                                        <p><strong>Total Amount:</strong> ৳{parseFloat(selectedOrder.total || 0).toFixed(2)}</p>
                                        {selectedOrder.promo_code && (
                                            <p><strong>Promo Code:</strong> {selectedOrder.promo_code}</p>
                                        )}
                                        {selectedOrder.payment_reference && (
                                            <p><strong>Payment Reference:</strong> {selectedOrder.payment_reference}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Customer Information */}
                                <div>
                                    <h4 className="font-semibold mb-3">Delivery Information</h4>
                                    <div className="space-y-2 text-sm">
                                        <p><strong>Name:</strong> {selectedOrder.delivery_name || 'N/A'}</p>
                                        <p><strong>Phone:</strong> {selectedOrder.delivery_phone || 'N/A'}</p>
                                        <p><strong>Address:</strong> {selectedOrder.delivery_address || 'N/A'}</p>
                                        <p><strong>City:</strong> {selectedOrder.delivery_city || 'N/A'}</p>
                                        <p><strong>Postal Code:</strong> {selectedOrder.delivery_postal_code || 'N/A'}</p>
                                        {selectedOrder.notes && (
                                            <p><strong>Notes:</strong> {selectedOrder.notes}</p>
                                        )}
                                    </div>

                                    {/* Payment Information */}
                                    {selectedOrder.book_payment && (
                                        <div className="mt-6">
                                            <h4 className="font-semibold mb-3">Payment Information</h4>
                                            <div className="space-y-2 text-sm">
                                                <p><strong>Account Name:</strong> {selectedOrder.book_payment.account_name || 'N/A'}</p>
                                                <p><strong>Amount:</strong> ৳{parseFloat(selectedOrder.book_payment.amount || 0).toFixed(2)}</p>
                                                <p><strong>Fee:</strong> ৳{parseFloat(selectedOrder.book_payment.fee || 0).toFixed(2)}</p>
                                                <p><strong>Charged Amount:</strong> ৳{parseFloat(selectedOrder.book_payment.charged_amount || 0).toFixed(2)}</p>
                                                {selectedOrder.book_payment.gateway_payment_id && (
                                                    <p><strong>Gateway Payment ID:</strong> {selectedOrder.book_payment.gateway_payment_id}</p>
                                                )}
                                                {selectedOrder.book_payment.gateway_transaction_id && (
                                                    <p><strong>Transaction ID:</strong> {selectedOrder.book_payment.gateway_transaction_id}</p>
                                                )}
                                                <p><strong>Initiated At:</strong> {selectedOrder.book_payment.initiated_at || 'N/A'}</p>
                                                {selectedOrder.book_payment.completed_at && (
                                                    <p><strong>Completed At:</strong> {selectedOrder.book_payment.completed_at}</p>
                                                )}
                                                {selectedOrder.book_payment.notes && (
                                                    <p><strong>Payment Notes:</strong> {selectedOrder.book_payment.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            {selectedOrder.items && selectedOrder.items.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="font-semibold mb-3">Order Items</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full border border-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Book</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {selectedOrder.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="px-4 py-2">
                                                            {item.book_image ? (
                                                                <img
                                                                    src={item.book_image}
                                                                    alt={item.book_name}
                                                                    className="w-12 h-16 object-cover rounded border"
                                                                />
                                                            ) : (
                                                                <div className="w-12 h-16 bg-gray-200 rounded border flex items-center justify-center">
                                                                    <span className="text-xs text-gray-500">No Image</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 text-sm">{item.book_name || 'N/A'}</td>
                                                        <td className="px-4 py-2 text-sm">{item.book_author || 'N/A'}</td>
                                                        <td className="px-4 py-2 text-sm">{item.quantity}</td>
                                                        <td className="px-4 py-2 text-sm">৳{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                                                        <td className="px-4 py-2 text-sm">৳{parseFloat(item.total_price || 0).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default OrderManagement